import React, { Fragment, useMemo } from 'react';
import Currency from 'react-currency-formatter';
import { useTranslation } from 'react-i18next';

// material-ui
import { TableCell, TableRow } from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Warning } from '@mui/icons-material';
import { useLanguageReference } from 'services/Hooks/useLanguageReference';
import { useAllInventoryItems } from 'services/Data/InventoryItemServices';
import InventoryItemCategoryEnum, { InventoryItemCategoryEnumValue } from 'enums/InventoryItemCategoryEnum';
import { arrayUnique } from 'services/Utilities/Utilities';
import ThemedTooltip from '@acnb/CustomComponents/Tooltip/ThemedTooltip';

// Icons

// Styles

// Actions

// Components

// Others

const useStyles = makeStyles(theme => ({
  // ...customGlobalStyle(theme),
  toolingCell: {
    maxWidth: '180px',
    textTransform: 'uppercase',
    borderRight: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
  },
  packagePrice: {
    borderLeft: '1px solid rgba(224, 224, 224, 1)',
    fontSize: 'medium',
    textAlign: 'center',
  },
  tableRow: {
    '& td': {
      padding: '10px',
    },
  },
  projectTollingBottomBorder: {
    borderBottom: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectToolingLeftBorder: {
    borderLeft: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectToolingTopBorder: {
    borderTop: '0.15rem solid rgba(169, 169, 169, 1)',
  },
  projectToolingRightBorder: {
    borderRight: '0.15rem solid rgba(169, 169, 169, 1)',
  },
}));

const DealerCopyGroupedToolingSection = ({
  projectInventoryItems,
  projectToolings,
  headerColSpan,
  projectCurrency,
  setWarningModalInfo,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const {
    labelReference,
    descriptionReference,
    nameReference,
    warningReference,
    warningReferenceFallback,
  } = useLanguageReference();

  const { data: availableInventoryItems } = useAllInventoryItems();

  const notNestedProjectToolings = useMemo(() => projectToolings?.filter(pi => !pi.parentProjectTooling_Id) || [], [
    projectToolings,
  ]);

  const toolingInventoryItemMap = useMemo(() => {
    const map = {};

    notNestedProjectToolings.forEach(projectTooling => {
      if (!map[projectTooling.tooling_Id]) {
        map[projectTooling.tooling_Id] = [];
      }

      const associatedProjectInventoryItems = projectInventoryItems.filter(
        pii => pii.projectTooling_Id === projectTooling.id,
      );

      for (let index = 0; index < associatedProjectInventoryItems.length; index++) {
        const element = associatedProjectInventoryItems[index];

        if (map[projectTooling.tooling_Id].every(pii => pii !== element)) map[projectTooling.tooling_Id].push(element);
      }

      const linkedToolingIds = projectToolings
        .filter(pt => pt.parentProjectTooling_Id === projectTooling.id)
        ?.map(pt => pt.id);

      const linkedToolingProjectInventoryItems = projectInventoryItems.filter(pii =>
        linkedToolingIds?.includes(pii.projectTooling_Id),
      );

      for (let index = 0; index < linkedToolingProjectInventoryItems.length; index++) {
        const element = linkedToolingProjectInventoryItems[index];

        if (map[projectTooling.tooling_Id].every(pii => pii !== element)) map[projectTooling.tooling_Id].push(element);
      }
    });

    return map;
  }, [notNestedProjectToolings, projectInventoryItems, projectToolings]);

  return (
    <>
      <TableRow>
        <TableCell colSpan={headerColSpan} style={{ backgroundColor: '#c9c7c7' }}>
          <strong>{t('toolings')}</strong>
        </TableCell>
      </TableRow>
      {notNestedProjectToolings?.map(tooling => {
        const currentToolingProjectInventoryItems = toolingInventoryItemMap[tooling.tooling_Id]?.filter(
          projectInventoryItem => projectInventoryItem.quantity,
        );

        const totalForSection = currentToolingProjectInventoryItems?.reduce(
          (acc, cur) => acc + cur.projectDealerTotalPrice,
          0,
        );

        const toolingInventoryItemCategories = arrayUnique(
          currentToolingProjectInventoryItems?.map(i => i.inventoryItemCategory) || [],
        );

        const totalRowSpan =
          (currentToolingProjectInventoryItems?.length || 0) + (toolingInventoryItemCategories?.length || 0);

        return toolingInventoryItemCategories
          .sort((a, b) => a - b)
          .map((categoryEnum, catIndex) => {
            let category = InventoryItemCategoryEnum.find(ii => ii.value === Number(categoryEnum));
            let categoryLabel = category?.[labelReference];

            const currentProjectWallCategoryItems = currentToolingProjectInventoryItems?.filter(
              i => i.inventoryItemCategory === categoryEnum,
            );

            const isLastCategory = toolingInventoryItemCategories.length - 1 === catIndex;

            return (
              <Fragment key={categoryEnum}>
                <TableRow>
                  {catIndex === 0 && (
                    <TableCell
                      className={`${classes.toolingCell} ${classes.projectTollingBottomBorder} ${classes.projectToolingTopBorder} ${classes.projectToolingLeftBorder}`}
                      rowSpan={totalRowSpan}>
                      {`${tooling[nameReference]}`}
                    </TableCell>
                  )}

                  {categoryEnum === InventoryItemCategoryEnumValue.Graphic ? (
                    <TableCell
                      className={catIndex === 0 ? classes.projectToolingTopBorder : ''}
                      colSpan={headerColSpan - 2}
                      style={{ backgroundColor: '#c9c7c7' }}>
                      <strong>{categoryLabel}</strong>
                    </TableCell>
                  ) : (
                    <TableCell
                      className={catIndex === 0 ? classes.projectToolingTopBorder : ''}
                      colSpan={headerColSpan - 2}
                      style={{ maxHeight: 0, height: 0, padding: 0 }}></TableCell>
                  )}

                  {catIndex === 0 && (
                    <TableCell
                      className={`${classes.packagePrice} ${classes.projectTollingBottomBorder} ${classes.projectToolingTopBorder} ${classes.projectToolingRightBorder}`}
                      rowSpan={totalRowSpan}>
                      <Currency quantity={totalForSection} currency={projectCurrency} />
                    </TableCell>
                  )}
                </TableRow>

                {currentProjectWallCategoryItems?.map((projectInventoryItem, itemIndex) => {
                  let inventoryItem = availableInventoryItems?.find(as => as.id === projectInventoryItem.inventoryItem_Id);

                  const isLastItem = currentProjectWallCategoryItems.length - 1 === itemIndex;

                  return (
                    <TableRow className={classes.tableRow} key={projectInventoryItem.id}>
                      <TableCell className={isLastCategory && isLastItem ? classes.projectTollingBottomBorder : ''}>
                        {(inventoryItem?.[warningReference] || inventoryItem?.[warningReferenceFallback]) && (
                          <ThemedTooltip
                            title={inventoryItem?.[warningReference] || inventoryItem?.[warningReferenceFallback]}
                            placement={'top'}>
                            <Warning
                              color="warning"
                              style={{ cursor: 'pointer' }}
                              sx={{ mr: 1 }}
                              onClick={() => {
                                setWarningModalInfo({ open: true, inventoryItem });
                              }}
                            />
                          </ThemedTooltip>
                        )}
                        <strong>{`${inventoryItem?.sku}`}</strong>
                      </TableCell>
                      <TableCell className={isLastCategory && isLastItem ? classes.projectTollingBottomBorder : ''}>
                        {projectInventoryItem?.[descriptionReference]}
                      </TableCell>

                      <TableCell
                        align="center"
                        className={isLastCategory && isLastItem ? classes.projectTollingBottomBorder : ''}>
                        {projectInventoryItem.quantity || '0'}
                      </TableCell>

                      <TableCell
                        align="right"
                        className={isLastCategory && isLastItem ? classes.projectTollingBottomBorder : ''}>
                        {projectInventoryItem.projectDealerUnitPrice === 0 ? (
                          '-'
                        ) : (
                          <Currency quantity={projectInventoryItem.projectDealerUnitPrice} currency={projectCurrency} />
                        )}
                      </TableCell>
                      <TableCell
                        align="right"
                        className={isLastCategory && isLastItem ? classes.projectTollingBottomBorder : ''}>
                        {projectInventoryItem.projectDealerTotalPrice === 0 ? (
                          '-'
                        ) : (
                          <Currency quantity={projectInventoryItem.projectDealerTotalPrice} currency={projectCurrency} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </Fragment>
            );
          });
      })}
    </>
  );
};

export default DealerCopyGroupedToolingSection;
